import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../lib/shadcn/card'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { toast } from '../../lib/shadcn/sonner'
import {
  useGetWholesaleTierMinimums,
  useSetFaireMinimum,
  useUpdateWholesaleTierMinimum,
} from '../../hooks/backend/settingsPage'
import { HelpText } from '../../components/HelpText'
import { pushFaireMinimums, type FaireTierProduct } from '../../utils/pushFaireMinimums'

interface TierMinimum {
  tier: string
  min_qty: number | null
  synced_at: string | null
  sync_error: string | null
  // undefined when B2B couldn't be read
  on_b2b?: boolean
  b2b_min_qty?: number | null
}

interface MinimumsData {
  b2bError: string | null
  b2bMode: string | null
  tiers: TierMinimum[]
}

interface SaveResult {
  synced: boolean
  error: string | null
  faire_products?: FaireTierProduct[]
}

const fmt = (n: number | null | undefined) => (n === null || n === undefined ? 'none' : n.toLocaleString('en-US'))

export default function WholesaleTierMinimumsSettings() {
  const getMinimums = useGetWholesaleTierMinimums()
  const updateMinimum = useUpdateWholesaleTierMinimum()
  const setFaireMinimum = useSetFaireMinimum()
  const data = getMinimums.data as MinimumsData | undefined

  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState<string | null>(null)

  const load = () => void getMinimums.trigger({}, { skipCache: true })

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!data) return
    const next: Record<string, string> = {}
    for (const t of data.tiers) {
      // Start from what B2B has until a minimum is saved here.
      const value = t.min_qty ?? t.b2b_min_qty ?? null
      next[t.tier] = value === null ? '' : String(value)
    }
    setDrafts(next)
  }, [data])

  const save = async (tier: string) => {
    const minQty = Number((drafts[tier] ?? '').trim())
    if (!Number.isInteger(minQty) || minQty < 1) {
      toast.error('Minimum must be a whole number of 1 or more')
      return
    }
    try {
      setBusy(tier)
      const result = (await updateMinimum.trigger({ tier, min_qty: minQty })) as SaveResult
      if (result.synced) {
        toast.success(`${tier} minimum saved and synced to B2B`)
      } else {
        toast.error(`${tier} minimum saved, but B2B didn't update: ${result.error ?? 'unknown error'}`)
      }
      load()
      setBusy(null)
      // Faire runs after the button frees up; its own toast shows progress.
      await pushFaireMinimums(tier, minQty, result.faire_products ?? [], params =>
        setFaireMinimum.trigger(params),
      )
    } catch {
      toast.error('Failed to save the minimum')
    } finally {
      setBusy(null)
    }
  }

  return (
    <Card id="wholesale-tier-minimums">
      <CardHeader>
        <CardTitle>Wholesale Tier Minimums</CardTitle>
        <CardDescription><HelpText>
          The minimum quantity a wholesale customer must order of each product in a tier on the
          B2B site. Small and Large bags of a flavor count together. Saving a minimum updates B2B
          right away, then sets the Faire minimum on every product in that tier that's on Faire
          (rounded up to whole cases of 6). B2B has minimums for Core, Limited, Special Order and Seasonal; the overall
          order minimum stays in WooCommerce &gt; Wholesale.
        </HelpText></CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {data?.b2bError && (
          <p className="text-sm text-destructive">Couldn't read B2B: {data.b2bError}</p>
        )}
        {data?.b2bMode === 'variations' && (
          <p className="text-sm text-amber-700 dark:text-amber-300">
            B2B is set to check each variation separately, so tier minimums aren't enforced. Change
            Product minimum method to Product tiers in WooCommerce &gt; Wholesale &gt; Minimums to use
            them.
          </p>
        )}
        {getMinimums.error && !data ? (
          <p className="text-sm text-destructive">Couldn't load tier minimums.</p>
        ) : !data ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : data.tiers.length === 0 ? (
          <p className="text-sm text-muted-foreground">Add a wholesale tier above first.</p>
        ) : (
          <div className="divide-y rounded-md border">
            {data.tiers.map(t => {
              const outOfSync =
                t.b2b_min_qty !== undefined && t.min_qty !== null && t.b2b_min_qty !== t.min_qty
              return (
                <div
                  key={t.tier}
                  className="grid grid-cols-1 gap-3 px-3 py-3 sm:grid-cols-[8rem,9rem,1fr,auto] sm:items-end"
                >
                  <span className="font-medium sm:pb-2">{t.tier}</span>
                  <div className="space-y-1.5">
                    <Label htmlFor={`tier-min-${t.tier}`}>Minimum</Label>
                    <Input
                      id={`tier-min-${t.tier}`}
                      type="number"
                      min="1"
                      step="1"
                      value={drafts[t.tier] ?? ''}
                      onChange={e => setDrafts(d => ({ ...d, [t.tier]: e.target.value }))}
                      className="h-9"
                    />
                  </div>
                  <div className="text-sm sm:pb-2">
                    {t.on_b2b === false ? (
                      <span className="text-muted-foreground">Not a tier on B2B, so nothing to sync.</span>
                    ) : t.sync_error ? (
                      <span className="text-destructive">Not synced: {t.sync_error}</span>
                    ) : t.b2b_min_qty === undefined ? (
                      <span className="text-muted-foreground">
                        {t.synced_at ? `Synced ${new Date(t.synced_at).toLocaleString()}` : 'Not synced yet'}
                      </span>
                    ) : outOfSync ? (
                      <span className="text-amber-700 dark:text-amber-300">B2B has {fmt(t.b2b_min_qty)}. Save to sync.</span>
                    ) : (
                      <span className="text-muted-foreground">B2B: {fmt(t.b2b_min_qty)}</span>
                    )}
                  </div>
                  <Button
                    type="button"
                    onClick={() => void save(t.tier)}
                    disabled={busy === t.tier || t.on_b2b === false}
                    className="gap-1.5 shrink-0"
                  >
                    {busy === t.tier && <Loader2 className="h-4 w-4 animate-spin" />}
                    Save
                  </Button>
                </div>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
